import { useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowRight, ArrowLeft, Clock, Calendar, MapPin, Mail, AlertTriangle } from 'lucide-react';
import { getFieldNoteBySlug } from '../data/field-notes';
import SEO from '../components/SEO';
import NewsletterForm from '../components/NewsletterForm';

const DISCOVERY_CALL_URL = 'https://calendly.com/nicolas-gate33/discovery-call';

const InsightArticlePage = () => {
  const { slug } = useParams();
  const article = getFieldNoteBySlug(slug);

  useEffect(() => {
    if (article) document.title = `${article.title} · Gate 33`;
    window.scrollTo(0, 0);
  }, [article]);

  if (!article) return <Navigate to="/insights" replace />;

  return (
    <div data-testid="insight-article-page" className="min-h-screen bg-white">
      <SEO
        title={article.title}
        description={article.dek}
        path={`/insights/field-notes/${article.slug}`}
        image={article.hero}
        type="article"
      />
      {/* Back link */}
      <div className="max-w-5xl mx-auto px-6 md:px-12 pt-10">
        <Link
          to="/insights"
          data-testid="article-back-link"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#5A5A5A] hover:text-[#C5A880] transition-colors"
        >
          <ArrowLeft size={14} /> Back to Insights
        </Link>
      </div>

      {/* ===== HERO ===== */}
      <section data-testid="article-hero" className="max-w-5xl mx-auto px-6 md:px-12 pt-12 pb-16">
        <div className="mb-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs uppercase tracking-[0.22em] text-[#5A5A5A]">
          <span className="text-[#C5A880] font-semibold">{article.category}</span>
          <span className="flex items-center gap-1.5"><Calendar size={12} /> {article.date}</span>
          <span className="flex items-center gap-1.5"><Clock size={12} /> {article.readTime}</span>
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-[#0F0F0F] mb-8 max-w-4xl">
          {article.title}
        </h1>
        <p className="font-heading text-xl sm:text-2xl leading-relaxed italic text-[#5A5A5A] max-w-3xl">
          {article.dek}
        </p>
      </section>

      {/* ===== HERO IMAGE ===== */}
      {article.hero && (
        <section className="max-w-6xl mx-auto px-6 md:px-12 mb-16">
          <figure>
            <div className="aspect-[21/10] overflow-hidden border border-[#E5E0D8]">
              <img
                src={article.hero}
                alt={article.heroAlt || article.title}
                className="w-full h-full object-cover"
              />
            </div>
            {article.heroCaption && (
              <figcaption className="mt-4 text-sm text-[#5A5A5A] italic pl-4 border-l-2 border-[#C5A880]">
                {article.heroCaption}
              </figcaption>
            )}
          </figure>
        </section>
      )}

      {/* ===== INTRO ===== */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 mb-20">
        {article.intro.map((p, i) => (
          <p
            key={i}
            className={`text-[#1A1A1A] leading-relaxed mb-6 ${i === 0 ? 'text-xl' : 'text-lg'}`}
            dangerouslySetInnerHTML={{ __html: p }}
          />
        ))}
      </section>

      {/* ===== BLOCKS — infographic-style ===== */}
      <section data-testid="article-mistakes" className="bg-[#FDFBF7] border-y border-[#E5E0D8] py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 mb-14 text-center">
          <span className="overline inline-block mb-4">
            {article.blocksOverline || 'The five mistakes'}
          </span>
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight text-[#0F0F0F]">
            {article.blocksHeading ||
              "If any of these look familiar, you're losing opportunities before a recruiter reads a single line."}
          </h2>
        </div>

        <div className="max-w-4xl mx-auto px-6 md:px-12 space-y-8">
          {article.mistakes.map((m) => (
            <article
              key={m.n}
              data-testid={`article-mistake-${m.n}`}
              className="bg-white border border-[#E5E0D8] p-8 md:p-10 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 md:gap-10"
            >
              <div className="flex flex-col items-start md:items-center gap-2 md:pt-1">
                <span
                  className="font-heading text-5xl md:text-6xl text-[#C5A880] leading-none tracking-tight"
                  aria-hidden
                >
                  {m.n}
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880]">
                  {article.blockLabel || 'Mistake'}
                </span>
              </div>
              <div>
                <h3 className="font-heading text-2xl text-[#0F0F0F] mb-4 leading-snug">{m.title}</h3>
                {m.body.map((para, i) => (
                  <p
                    key={i}
                    className="text-[#5A5A5A] leading-relaxed mb-4"
                    dangerouslySetInnerHTML={{ __html: para }}
                  />
                ))}
                {m.insight && (
                  <div className="mt-6 pl-5 border-l-2 border-[#C5A880]">
                    <p className="font-heading italic text-lg text-[#0F0F0F] leading-relaxed">
                      {m.insight}
                    </p>
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* ===== CLOSING ===== */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20">
        <span className="overline inline-block mb-4">The turning point</span>
        <h2 className="font-heading text-3xl sm:text-4xl tracking-tight leading-tight mb-8 text-[#0F0F0F]">
          {article.closingHeading}
        </h2>
        {article.closing.map((p, i) => (
          <p
            key={i}
            className="text-[#1A1A1A] leading-relaxed mb-6 text-lg"
            dangerouslySetInnerHTML={{ __html: p }}
          />
        ))}
      </section>

      {/* ===== LIVE OPPORTUNITY CARD ===== */}
      {article.liveOpportunity && (
        <section data-testid="article-live-opportunity" className="section-dark py-20">
          <div className="max-w-3xl mx-auto px-6 md:px-12">
            <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-4">
              {article.liveOpportunity.label}
            </span>
            <h3 className="font-heading text-2xl sm:text-3xl text-[#FDFBF7] leading-tight mb-6">
              {article.liveOpportunity.title}
            </h3>
            <ul className="space-y-2.5 mb-8">
              {article.liveOpportunity.criteria.map((c) => (
                <li key={c} className="flex items-start gap-3 text-sm text-[#D6D2CA]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C5A880] mt-2 flex-shrink-0" />
                  <span>{c}</span>
                </li>
              ))}
            </ul>
            <div className="border border-[#C5A880] p-6 mb-6">
              <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-x-6 gap-y-3 text-sm">
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A880] font-semibold">Apply to</span>
                <a href={`mailto:${article.liveOpportunity.apply}?subject=${encodeURIComponent(article.liveOpportunity.subject)}`}
                   className="text-[#FDFBF7] font-mono hover:text-[#C5A880] transition-colors">
                  <Mail size={14} className="inline mr-2" />
                  {article.liveOpportunity.apply}
                </a>
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#C5A880] font-semibold">Subject line</span>
                <code className="text-[#FDFBF7] font-mono bg-black/40 px-3 py-1 self-start">
                  {article.liveOpportunity.subject}
                </code>
              </div>
            </div>
            <div className="flex gap-4 items-start p-5 bg-[#C5A880]/10 border border-[#C5A880]/40">
              <AlertTriangle size={18} className="text-[#C5A880] flex-shrink-0 mt-0.5" />
              <p className="text-sm text-[#D6D2CA] leading-relaxed italic">
                {article.liveOpportunity.note}
              </p>
            </div>
          </div>
        </section>
      )}

      {/* ===== INTERNAL CTA (assessment, program etc.) ===== */}
      {article.internalCta && (
        <section data-testid="article-internal-cta" className="section-dark py-20">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            {article.internalCta.overline && (
              <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-4">
                {article.internalCta.overline}
              </span>
            )}
            <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] leading-tight mb-6">
              {article.internalCta.title}
            </h3>
            {article.internalCta.description && (
              <p className="text-[#D6D2CA] leading-relaxed mb-10 max-w-2xl mx-auto">
                {article.internalCta.description}
              </p>
            )}
            {/^https?:/.test(article.internalCta.buttonHref) ? (
            <a
              href={article.internalCta.buttonHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="article-internal-cta-button"
              className="btn-primary inline-flex items-center gap-2"
            >
              {article.internalCta.buttonText} <ArrowRight size={16} />
            </a>
            ) : (
            <Link
              to={article.internalCta.buttonHref}
              data-testid="article-internal-cta-button"
              className="btn-primary inline-flex items-center gap-2"
            >
              {article.internalCta.buttonText} <ArrowRight size={16} />
            </Link>
            )}
            {article.internalCta.footnote && (
              <p className="mt-6 text-xs uppercase tracking-[0.2em] text-[#C5A880]/80">
                {article.internalCta.footnote}
              </p>
            )}
          </div>
        </section>
      )}

      {/* ===== NEWSLETTER (article-end capture) ===== */}
      <section data-testid="article-newsletter" className="bg-[#FDFBF7] border-y border-[#E5E0D8]">
        <div className="max-w-3xl mx-auto px-6 md:px-12 py-16">
          <div className="max-w-xl">
            <span className="overline inline-block mb-3">Newsletter</span>
            <h3 className="font-heading text-2xl sm:text-3xl text-[#0F0F0F] leading-tight mb-3">
              Get operator news, cohort dates &amp; career insights.
            </h3>
            <p className="text-sm text-[#5A5A5A] mb-6">
              Sent by Nicolas. Low frequency, no spam — unsubscribe anytime.
            </p>
            <NewsletterForm source="article" variant="light" compact testId="article-newsletter-form" />
          </div>
        </div>
      </section>

      {/* ===== READINESS ASSESSMENT CTA — every article ===== */}
      <section data-testid="article-readiness-cta" className="section-dark py-20">
        <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
          <span className="text-[10px] uppercase tracking-[0.22em] text-[#C5A880] font-semibold inline-block mb-4">
            Free · 15 minutes · Manually reviewed
          </span>
          <h3 className="font-heading text-2xl sm:text-3xl lg:text-4xl text-[#FDFBF7] leading-tight mb-6">
            Where does your profile actually sit today?
          </h3>
          <p className="text-[#9A9A9A] leading-relaxed mb-10 max-w-xl mx-auto">
            Twelve real-world scenarios plus three operator cases — reviewed personally by Nicolas.
            Written feedback on where you already stand and what still needs work before the next application.
          </p>
          <Link
            to="/operational-assessment"
            data-testid="article-readiness-cta-button"
            className="inline-flex items-center gap-2 bg-[#C5A880] text-[#0F0F0F] px-10 py-4 uppercase tracking-[0.15em] text-xs font-semibold hover:bg-[#FDFBF7] transition-all duration-300"
          >
            Take the Free Readiness Assessment <ArrowRight size={16} />
          </Link>
        </div>
      </section>

      {/* ===== CTA ===== */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 py-20 text-center">
        <span className="overline inline-block mb-4">Need advice?</span>
        <h3 className="font-heading text-2xl sm:text-3xl tracking-tight leading-tight mb-6 text-[#0F0F0F]">
          Should you need advice from us, please feel free to reach out.
        </h3>
        <a
          href={DISCOVERY_CALL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="article-cta"
          className="btn-primary inline-flex items-center gap-2"
        >
          Book a Free Discovery Call <ArrowRight size={16} />
        </a>
      </section>
    </div>
  );
};

export default InsightArticlePage;
